import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useProfile } from '../contexts/ProfileContext'
import { supabase } from '../lib/supabase'
import {
  AlertTriangle,
  Archive,
  Bell,
  Briefcase,
  Check,
  CheckCircle2,
  ClipboardList,
  Clock,
  LogIn,
  ShieldCheck,
  Trash2,
  UserCheck,
  Users,
  X,
} from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

type NotificationType =
  | 'task_assigned'
  | 'task_due_soon'
  | 'task_overdue'
  | 'onboarding_starting_soon'
  | 'invite_accepted'
  | 'portal_completed'
  | 'blocker_flagged'
  | 'task_completed'
  | 'employee_added'
  | 'onboarding_completed'
  | 'form_submitted'

interface Notification {
  id: string
  type: NotificationType
  title: string
  message: string
  action_url: string | null
  task_id: string | null
  employee_id: string | null
  read: boolean
  archived: boolean
  created_at: string
  sender?: { name: string; email: string } | null
  employee?: { name: string } | null
}

// ─── Notification type config ─────────────────────────────────────────────────

interface TypeConfig {
  icon: React.ReactNode
  iconBg: string
  color: string
}

const TYPE_CONFIG: Record<string, TypeConfig> = {
  task_assigned: {
    icon: <Briefcase className="w-3.5 h-3.5" />,
    iconBg: 'bg-[#EEF2FF]',
    color: 'text-accent',
  },
  task_due_soon: {
    icon: <Clock className="w-3.5 h-3.5" />,
    iconBg: 'bg-amber-50',
    color: 'text-amber-600',
  },
  task_overdue: {
    icon: <AlertTriangle className="w-3.5 h-3.5" />,
    iconBg: 'bg-red-50',
    color: 'text-red-500',
  },
  invite_accepted: {
    icon: <LogIn className="w-3.5 h-3.5" />,
    iconBg: 'bg-emerald-50',
    color: 'text-emerald-600',
  },
  portal_completed: {
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    iconBg: 'bg-emerald-50',
    color: 'text-emerald-600',
  },
  onboarding_starting_soon: {
    icon: <UserCheck className="w-3.5 h-3.5" />,
    iconBg: 'bg-[#EEF2FF]',
    color: 'text-accent',
  },
  blocker_flagged: {
    icon: <AlertTriangle className="w-3.5 h-3.5" />,
    iconBg: 'bg-red-50',
    color: 'text-red-500',
  },
  task_completed: {
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    iconBg: 'bg-emerald-50',
    color: 'text-emerald-600',
  },
  employee_added: {
    icon: <Users className="w-3.5 h-3.5" />,
    iconBg: 'bg-[#EEF2FF]',
    color: 'text-accent',
  },
  onboarding_completed: {
    icon: <ShieldCheck className="w-3.5 h-3.5" />,
    iconBg: 'bg-emerald-50',
    color: 'text-emerald-600',
  },
  form_submitted: {
    icon: <ClipboardList className="w-3.5 h-3.5" />,
    iconBg: 'bg-[#EEF2FF]',
    color: 'text-accent',
  },
}

const FALLBACK_CONFIG: TypeConfig = {
  icon: <Bell className="w-3.5 h-3.5" />,
  iconBg: 'bg-slate-100',
  color: 'text-slate-500',
}

function getTypeConfig(type: string): TypeConfig {
  return TYPE_CONFIG[type] ?? FALLBACK_CONFIG
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now  = new Date()
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diff < 60)      return 'Just now'
  if (diff < 3600)    return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400)   return `${Math.floor(diff / 3600)}h ago`
  if (diff < 604800)  return `${Math.floor(diff / 86400)}d ago`

  return date.toLocaleDateString('en-GB', {
    day:   'numeric',
    month: 'short',
    year:  date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  })
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function NotificationCenter() {
  const { profile }   = useProfile()
  const navigate      = useNavigate()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount,   setUnreadCount]   = useState(0)
  const [isOpen,        setIsOpen]        = useState(false)
  const [loading,       setLoading]       = useState(false)
  const [fetchError,    setFetchError]    = useState<string | null>(null)
  const [filter,        setFilter]        = useState<'all' | 'unread'>('all')
  const [actionMenu,    setActionMenu]    = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<Set<string>>(new Set())

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchNotifications = useCallback(async () => {
    if (!profile?.id) return
    setFetchError(null)

    const { data, error } = await supabase
      .from('notifications')
      .select(`
        *,
        sender:sender_id(name, email),
        employee:employee_id(name)
      `)
      .eq('recipient_id', profile.id)
      .eq('archived', false)
      .order('created_at', { ascending: false })
      .limit(25)

    if (error) {
      console.error('[NotificationCenter] fetch error:', error)
      setFetchError(error.message)
      return
    }

    const formatted = (data ?? []).map((n) => ({
      ...n,
      sender:   n.sender   ?? null,
      employee: n.employee ?? null,
    })) as Notification[]

    setNotifications(formatted)
    setUnreadCount(formatted.filter((n) => !n.read).length)
  }, [profile?.id])

  useEffect(() => {
    if (profile?.id) {
      fetchNotifications()
    }
  }, [profile?.id, fetchNotifications])

  // ── Realtime subscription ─────────────────────────────────────────────────

  useEffect(() => {
    if (!profile?.id) return

    const channel = supabase
      .channel(`notifications:${profile.id}`)
      .on(
        'postgres_changes',
        {
          event:  'INSERT',
          schema: 'public',
          table:  'notifications',
          filter: `recipient_id=eq.${profile.id}`,
        },
        (payload) => {
          const n = payload.new as Notification
          setNotifications((prev) => [n, ...prev].slice(0, 25))
          setUnreadCount((prev) => prev + 1)
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [profile?.id])

  // ── Actions ────────────────────────────────────────────────────────────────

  const markAsRead = async (id: string) => {
    setActionLoading((prev) => new Set([...prev, id]))
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true, read_at: new Date().toISOString(), updated_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n))
      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch (e) {
      console.error('[NotificationCenter] markAsRead error:', e)
    } finally {
      setActionLoading((prev) => { const s = new Set(prev); s.delete(id); return s })
    }
  }

  const markAllAsRead = async () => {
    if (unreadCount === 0 || !profile?.id) return
    setLoading(true)
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true, read_at: new Date().toISOString(), updated_at: new Date().toISOString() })
        .eq('recipient_id', profile.id)
        .eq('read', false)
      if (error) throw error
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch (e) {
      console.error('[NotificationCenter] markAllAsRead error:', e)
    } finally {
      setLoading(false)
    }
  }

  const archiveNotification = async (id: string) => {
    setActionLoading((prev) => new Set([...prev, id]))
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ archived: true, updated_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error
      setNotifications((prev) => prev.filter((n) => n.id !== id))
      setUnreadCount((prev) => {
        const n = notifications.find((x) => x.id === id)
        return n && !n.read ? Math.max(0, prev - 1) : prev
      })
    } catch (e) {
      console.error('[NotificationCenter] archive error:', e)
    } finally {
      setActionLoading((prev) => { const s = new Set(prev); s.delete(id); return s })
    }
  }

  const deleteNotification = async (id: string) => {
    setActionLoading((prev) => new Set([...prev, id]))
    try {
      const { error } = await supabase.from('notifications').delete().eq('id', id)
      if (error) throw error
      setNotifications((prev) => prev.filter((n) => n.id !== id))
      setUnreadCount((prev) => {
        const n = notifications.find((x) => x.id === id)
        return n && !n.read ? Math.max(0, prev - 1) : prev
      })
    } catch (e) {
      console.error('[NotificationCenter] delete error:', e)
    } finally {
      setActionLoading((prev) => { const s = new Set(prev); s.delete(id); return s })
    }
  }

  // ── Click: mark read + navigate ───────────────────────────────────────────

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      await markAsRead(notification.id)
    }
    setIsOpen(false)
    setActionMenu(null)
    if (notification.action_url) {
      navigate(notification.action_url)
    }
  }

  // ── Filtered view ─────────────────────────────────────────────────────────

  const filtered = notifications.filter((n) =>
    filter === 'unread' ? !n.read : true,
  )

  // Don't render if not authenticated
  if (!profile) return null

  return (
    <div className="relative">
      {/* Bell button */}
      <button
        onClick={() => { setIsOpen((o) => !o); setActionMenu(null) }}
        className="relative p-2 text-slate-500 hover:text-brand hover:bg-slate-100 rounded-lg transition-colors"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-accent text-white text-[10px] font-semibold rounded-full flex items-center justify-center leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => { setIsOpen(false); setActionMenu(null) }}
          />

          {/* Panel */}
          <div className="absolute right-0 top-11 w-[380px] bg-white rounded-xl shadow-xl border border-slate-200/80 z-50 overflow-hidden flex flex-col max-h-[540px]">

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-accent/10 text-accent">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    disabled={loading}
                    className="text-xs text-slate-500 hover:text-brand font-medium px-2 py-1 rounded-md hover:bg-slate-50 transition-colors disabled:opacity-40"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Filter tabs */}
            <div className="flex gap-1 px-4 py-2 border-b border-slate-100 bg-background">
              {(['all', 'unread'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors capitalize ${
                    filter === f
                      ? 'bg-white text-foreground shadow-sm border border-slate-200'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-white/60'
                  }`}
                >
                  {f}
                  {f === 'unread' && unreadCount > 0 && (
                    <span className="ml-1.5 text-[10px] text-slate-400">{unreadCount}</span>
                  )}
                </button>
              ))}
            </div>

            {/* List */}
            <div className="overflow-y-auto flex-1 divide-y divide-slate-100">
              {fetchError ? (
                <div className="p-6 text-center">
                  <AlertTriangle className="w-5 h-5 text-red-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Failed to load notifications</p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="py-12 px-6 text-center">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                    <Bell className="w-5 h-5 text-slate-300" />
                  </div>
                  <p className="text-sm font-medium text-slate-500">
                    {filter === 'unread' ? "You're all caught up." : 'No notifications yet'}
                  </p>
                  {filter === 'unread' && (
                    <p className="text-xs text-slate-400 mt-1">Nothing new to review.</p>
                  )}
                </div>
              ) : (
                filtered.map((n) => {
                  const cfg = getTypeConfig(n.type)
                  return (
                    <div
                      key={n.id}
                      className={`group relative flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${
                        !n.read ? 'bg-background hover:bg-[#EEF2FF]/30' : 'hover:bg-slate-50'
                      }`}
                      onClick={() => handleNotificationClick(n)}
                    >
                      {/* Unread dot */}
                      {!n.read && (
                        <div className="absolute left-1.5 top-4 w-1.5 h-1.5 bg-accent rounded-full flex-shrink-0" />
                      )}

                      {/* Icon */}
                      <div className={`flex-shrink-0 w-7 h-7 rounded-full ${cfg.iconBg} flex items-center justify-center mt-0.5 ${cfg.color}`}>
                        {cfg.icon}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm leading-snug ${!n.read ? 'font-semibold text-foreground' : 'font-medium text-slate-700'}`}>
                            {n.title}
                          </p>
                          <span className="text-meta text-slate-400 whitespace-nowrap flex-shrink-0 mt-0.5">
                            {formatTimeAgo(n.created_at)}
                          </span>
                        </div>
                        {n.message && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2 leading-relaxed">
                            {n.message}
                          </p>
                        )}
                      </div>

                      {/* Action menu */}
                      <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setActionMenu(actionMenu === n.id ? null : n.id)
                            }}
                            className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md"
                          >
                            <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor">
                              <circle cx="6" cy="2" r="1" />
                              <circle cx="6" cy="6" r="1" />
                              <circle cx="6" cy="10" r="1" />
                            </svg>
                          </button>
                          {actionMenu === n.id && (
                            <div
                              className="absolute right-0 top-6 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-10 min-w-[132px]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {!n.read && (
                                <button
                                  onClick={() => { markAsRead(n.id); setActionMenu(null) }}
                                  className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 w-full text-left"
                                >
                                  <Check className="w-3 h-3" />
                                  Mark read
                                </button>
                              )}
                              <button
                                onClick={() => { archiveNotification(n.id); setActionMenu(null) }}
                                className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 w-full text-left"
                              >
                                <Archive className="w-3 h-3" />
                                Archive
                              </button>
                              <button
                                onClick={() => { deleteNotification(n.id); setActionMenu(null) }}
                                className="flex items-center gap-2 px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 w-full text-left"
                              >
                                <Trash2 className="w-3 h-3" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>

            {/* Footer */}
            {filtered.length > 0 && (
              <div className="px-4 py-2.5 border-t border-slate-100 bg-background">
                <p className="text-meta text-slate-400 text-center">
                  Showing {filtered.length} notification{filtered.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
